import { useInfiniteQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useRouter } from "next/router";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import InView from "react-intersection-observer";
import SwiperCore from "swiper";

import {
  FavoritesTypeEnum,
  Product,
  ProductAvailabilityCity,
  SettingsResponse,
} from "@/api/generatedTypes";
import ProductApi, { PRODUCTS_PER_PAGE } from "@/api/ProductsApi";
import { BaseCarousel } from "@/components/BaseCarousel";
import { BlockItem } from "@/components/BlockItem";
import { ChatLinkMenu } from "@/components/ChatLinkMenu";
import { useOpenChatChooseMenu } from "@/components/ChatLinkMenu/useOpenChatChooseMenu";
import { ColorsSection } from "@/components/ColorsSection";
import { FavoritesHandleButton } from "@/components/FavoritesHandleButton";
import { LayoutGray } from "@/components/LayoutGray";
import { MeasurementsModal } from "@/components/modals/MeasurementsModal";
import { OfflineAvailabilityModal } from "@/components/modals/OfflineAvailabilityModal";
import { ProductDetailsModal } from "@/components/modals/ProductDetailsModal";
import { ProductItem } from "@/components/ProductItem";
import { SeoTags } from "@/components/SeoTags";
import { useToastHandlers } from "@/components/toaster";
import { GoUpButton } from "@/components/ui-components/GoUpButton";
import { OnePlusOneLabel } from "@/components/ui-components/OnePlusOneLabel";
import { AddWaitingListButton } from "@/components/waiting-list/AddWaitingListButton";
import { useWaitingListHandlers } from "@/components/waiting-list/useWaitingListHandlers";
import { ymReachGoalHelper } from "@/components/YandexMetrica";
import { canOldPriceShow } from "@/helpers/canOldPriceShow";
import { getFormatedPrice } from "@/helpers/getFormatedPrice";
import { useAuth } from "@/hooks/useAuth";
import { useAddToCartHandler } from "@/hooks/useAddToCartHandler";
import { useProductGoUpListener } from "@/hooks/useGoUpListener";
import { useToggle } from "@/hooks/useToggle";
import { BonusesLabel } from "@/components/ui-components/BonusesLabel";

import { ChooseSizeSection } from "./ChooseSizeSection";
import styles from "./ProductPageComponent.module.scss";
import { SharePopup } from "./SharePopup";

interface ProductPageComponentProps {
  product: Product;
  slug: string;
  url: string;
  settings: SettingsResponse;
}

const ProductPageComponent = ({
  product,
  slug,
  url,
  settings
}: ProductPageComponentProps) => {
  const [isProductAdded, setIsProductAdded] = useState<boolean>(false);
  const [isOpenSharePopup, setIsOpenSharePopup] = useState<boolean>(false);

  const { openChatMenu, isChatMenuOpen, closeChatMenu } =
    useOpenChatChooseMenu();
  const router = useRouter();
  const { addToast } = useToastHandlers();
  const { addToWaitingList, waitingList, deleteFromWaitingList } =
    useWaitingListHandlers();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<SwiperCore | null | undefined>();

  const resetContainerScroll = () => {
    if (typeof window === "undefined") return;
    window.scrollTo(0, 0);
  };

  const isArrowShown = useProductGoUpListener(scrollContainerRef);

  const {
    isOpen: isProductDetailsModalOpen,
    close: closeProductDetailsModal,
    open: openProductDetailsModal,
  } = useToggle();
  const {
    isOpen: isOfflineAvailabilityModalOpen,
    close: closeOfflineAvailabilityModal,
    open: openOfflineAvailabilityModal,
  } = useToggle();
  const {
    isOpen: isMeasurementsOpen,
    close: closeMeasurements,
    open: openMeasurements,
  } = useToggle();

  const { addToCartHandler } = useAddToCartHandler(() =>
    setIsProductAdded(true)
  );

  useEffect(() => {
    const handleRouteChange = () => {
      if (!isProductAdded) {
        ymReachGoalHelper("exit-without-cart-add");
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(1, 300);
    }
    ymReachGoalHelper("product-card-view");
  }, [slug]);

  const similarProductsQuery = useInfiniteQuery(
    ["similarProducts", product.slug],
    ({ pageParam = 0 }) => {
      return ProductApi.similar({
        offset: pageParam,
        slug: product.slug,
      });
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) => {
        const itemsLength = lastPage.data.length;
        if (itemsLength === 12) return itemsLength * allPages.length;

        return undefined;
      },
    }
  );

  const onAvailabilityBtnClick = () => {
    ymReachGoalHelper("availability-click", {
      productName: product.name,
      productSlug: product.slug,
    });
    openOfflineAvailabilityModal();
  };

  const showSoldOutToster = () => {
    addToast({
      photo: product.slides?.[0].url || "",
      title: "SOLD OUT",
      text: "Товар распродан",
      variant: "product",
    });
  };

  const sizeIds = product.sizes.map((item) => item.id);
  const sizesInWaitingList =
    waitingList?.filter((item) => sizeIds.includes(item.size.id)) || [];
  const isInWaitingList = !!waitingList?.find(
    (item) => item.colored_model_id === product.id
  );

  const productLink = `https://lookonline.ru/?product=${product.slug}`;

  const outOfStockOnline = product.sizes.every(
    (item) => item.count_online === 0
  );
  const soldOut = getIsSoldOut(product.availability);

  const similarProducts = similarProductsQuery.data;

  const offlineAvaliability = product.availability.filter(
    (item) => item.city !== "Интернет магазин"
  );

  const isAuthorized = useAuth();

  const meta = {
    title: `${
      product?.alt_name ? product?.alt_name : product?.name
    } Look Online`,
    description: ` ${
      product?.alt_name ? product?.alt_name : product?.name
    } ${getFormatedPrice(product?.price)}, женская одежда`,
    url,
    image: product?.slides?.[0].url,
    keywords: `lookonline.ru, женская одежда, ${product.name}`,
  };

  const handleOpenSharePopup = async () => {
    if (isAuthorized) return setIsOpenSharePopup(true);

    window.localStorage.setItem("before_auth_url", router.asPath);
    router.push("/auth");

    return;
  };

  const handleCloseSharePopup = useCallback(() => {
    setIsOpenSharePopup(false)
  }, [])

  return (
    <LayoutGray isBackButton isProfileButton>
      <SeoTags meta={meta} />
      <div className={styles.root}>
        {product.one_plus_one && (
          <OnePlusOneLabel className={styles.onePlusOne} />
        )}
        {product.bonuses_allowed && (
          <BonusesLabel className={styles.onePlusOne} />
        )}
        {product.is_gift && (
          <OnePlusOneLabel className={styles.onePlusOne} title="ПОДАРОК"/>
        )}
        <div className={styles.vendorCode}>арт. {product.model_fullcode}</div>

        <BaseCarousel slides={product.slides} swiperRef={swiperRef} />
        
        {isOpenSharePopup && <SharePopup product={product} onClose={handleCloseSharePopup}/>}
        
        <div className={styles.nameSection}>
          <button className={styles.shareButton} onClick={openChatMenu}>
            <img src="/static/img/icons-v6/message.svg" alt="share button" />
          </button>

          { settings.wink_mails && (
            <button className={styles.shareButton} onClick={handleOpenSharePopup}>
              <img src="/static/img/icons-v6/share.svg" alt="share button" />
            </button>
          )}

          <div className={styles.namePrice}>
            <p className={styles.name}>{product.short_name}</p>
            <p className={styles.price}>
              {getFormatedPrice(product.price)}
              {canOldPriceShow(product) && (
                <span className={styles.oldPrice}>
                  {getFormatedPrice(product.old_price)}
                </span>
              )}
            </p>
          </div>
          <FavoritesHandleButton
            itemId={product.id}
            itemType={FavoritesTypeEnum.PRODUCT}
          />
        </div>

        <ColorsSection
          colors={product.colors}
          currentColorName={product.color}
          vendorCode={product.model_fullcode}
        />

        {!outOfStockOnline && !soldOut && (
          <ChooseSizeSection
            sizes={product.sizes}
            addToCartHandler={addToCartHandler}
            slug={slug}
          />
        )}

        {soldOut && (
          <div className={styles.sizeSection}>
            <button
              className={classNames(styles.borderedButton, styles.soldOut)}
              onClick={showSoldOutToster}
            >
              sold out
            </button>
            <AddWaitingListButton
              isInWaitingList={isInWaitingList}
              sizes={product.sizes}
              addToWaitingList={addToWaitingList}
              deleteFromWaitingList={deleteFromWaitingList}
              sizesInWaitingList={sizesInWaitingList}
            />
          </div>
        )}

        {outOfStockOnline && !soldOut && (
          <div className={styles.sizeSection}>
            <button
              className={classNames(
                styles.borderedButton,
                styles.outOfStockOnline
              )}
              onClick={openOfflineAvailabilityModal}
            >
              <p className={styles.outOfStockOnline_title}>
                недоступно для заказа онлайн
              </p>
              <p className={styles.outOfStockOnline_text}>
                смотреть наличие в офлайне
              </p>
            </button>
            <AddWaitingListButton
              isInWaitingList={isInWaitingList}
              sizes={product.sizes}
              addToWaitingList={addToWaitingList}
              deleteFromWaitingList={deleteFromWaitingList}
              sizesInWaitingList={sizesInWaitingList}
            />
          </div>
        )}

        <div className={styles.buttonsContainer}>
          <button
            className={styles.arrowButton}
            onClick={onAvailabilityBtnClick}
          >
            наличие в магазинах{" "}
            <img src="/static/img/icons-v6/back.svg" alt="arrow" />
          </button>
          <button
            className={styles.arrowButton}
            onClick={openProductDetailsModal}
          >
            о товаре <img src="/static/img/icons-v6/back.svg" alt="arrow" />
          </button>
          <button className={styles.arrowButton} onClick={openMeasurements}>
            размерная сетка{" "}
            <img src="/static/img/icons-v6/back.svg" alt="arrow" />
          </button>
        </div>
        <div className={styles.title}>more</div>
        <div className={styles.looksGrid} ref={scrollContainerRef}>
          {product.blocks_with_product.map((item, index) => (
            <div key={item.id}>
              <BlockItem block={item} index={index} />
              <p className={styles.lookTitle}>lookbook</p>
            </div>
          ))}

          {(!product.is_gift) && similarProducts?.pages &&
            similarProducts.pages.map((page, pageIndex, pageArray) =>
              page.data.map((productItem, index) => {
                const isLastPage = pageArray.length - 1 === pageIndex;
                const isMiddleOfThePage =
                  PRODUCTS_PER_PAGE / 2 + 1 === index + 1;

                return (
                  <Fragment key={productItem.id}>
                    {isLastPage && isMiddleOfThePage ? (
                      <InView
                        as="div"
                        onChange={(inView) =>
                          inView && similarProductsQuery.fetchNextPage()
                        }
                        className={styles.similarProduct}
                      >
                        <ProductItem product={productItem} />
                      </InView>
                    ) : (
                      <ProductItem
                        product={productItem}
                        className={styles.similarProduct}
                      />
                    )}
                  </Fragment>
                );
              })
            )}
        </div>
      </div>
      {isArrowShown && (
        <GoUpButton className={styles.goUp} onClick={resetContainerScroll} />
      )}
      <ChatLinkMenu
        isOpen={isChatMenuOpen}
        close={closeChatMenu}
        productLink={productLink}
      />
      <ProductDetailsModal
        product={product}
        close={closeProductDetailsModal}
        isOpen={isProductDetailsModalOpen}
      />
      <OfflineAvailabilityModal
        close={closeOfflineAvailabilityModal}
        availability={offlineAvaliability}
        isOpen={isOfflineAvailabilityModalOpen}
      />
      <MeasurementsModal
        measurements={product?.measurements}
        sizes={product?.sizes}
        close={closeMeasurements}
        isOpen={isMeasurementsOpen}
      />
    </LayoutGray>
  );
};

export default ProductPageComponent;

const getIsSoldOut = (availability: ProductAvailabilityCity[]) => {
  const allStoresFlat = availability.map((item) => item.stores).flat();

  const allSizesFlat = allStoresFlat.map((item) => item.sizes).flat();

  return allSizesFlat.every((item) => item.count === 0);
};
